import {
  IconBuilding,
  IconChevronRight,
  IconFolder,
  IconPlayerPlay,
  IconRobot,
} from "@tabler/icons-react";
import {
  useSuspenseQuery,
  useQuery,
} from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Link } from "@tanstack/react-router";
import { Suspense } from "react";

import { IssuesOverviewCard } from "@/dashboard/components/issues-overview-card";
import { PendingInvitationModal } from "@/dashboard/pending-invitation-modal";
import { getSortPreferenceServerFn } from "@/infrastructure/sort-preferences";
import { useTRPC } from "@/infrastructure/trpc/react";
import { CreateOrganizationDialog } from "@/organizations/components/create-organization-dialog";
import { CreateProjectDialog } from "@/organizations/components/create-project-dialog";
import { ProjectCard } from "@/projects/components/project-card";
import { AppLayout } from "@/shared/layout";
import { Badge } from "@/shared/ui/badge";
import { Button } from "@/shared/ui/button";
import { Card, CardContent } from "@/shared/ui/card";
import {
  Empty,
  EmptyHeader,
  EmptyTitle,
  EmptyDescription,
  EmptyContent,
} from "@/shared/ui/empty";
import { StatCard } from "@/shared/ui/stat-card";
import { OrganizationRoles } from "@vivus/common";

export const Route = createFileRoute("/app/dashboard")({
  loader: async ({ context }) => {
    const [orgsWithProjects, sortPreference] =
      await Promise.all([
        context.queryClient.query(
          context.trpc.organization.getOrganizationsDetails.queryOptions()
        ),
        getSortPreferenceServerFn({
          data: "dashboard",
        }),
      ]);

    // Prefetch agent stats - await to prevent hydration mismatch since it's displayed in StatCards
    await context.queryClient
      .query(
        context.trpc.agents.getDashboardStats.queryOptions()
      )
      .catch(() => undefined);

    // Secondary data - can load after initial render
    void context.queryClient
      .query(
        context.trpc.organizationInvitations.getPendingInvitationsForUser.queryOptions()
      )
      .catch(() => undefined);

    // Prefetch issue stats for dashboard
    void context.queryClient
      .query(
        context.trpc.agents.getDashboardIssueStats.queryOptions()
      )
      .catch(() => undefined);

    return { orgsWithProjects, sortPreference };
  },
  component: DashboardPage,
});

function DashboardPage() {
  const trpc = useTRPC();

  const { data: orgsWithProjects = [] } = useSuspenseQuery(
    trpc.organization.getOrganizationsDetails.queryOptions()
  );

  // Auto-refresh agent stats every 30 seconds for live activity updates
  const { data: agentStats } = useQuery({
    ...trpc.agents.getDashboardStats.queryOptions(),
    refetchInterval: 30_000,
    refetchIntervalInBackground: false,
  });

  const hasOrganizations = orgsWithProjects.length > 0;

  const totalProjects = orgsWithProjects.reduce(
    (acc, org) => acc + org.projects.length,
    0
  );

  return (
    <AppLayout
      title="Dashboard"
      description="Manage your organizations and projects"
    >
      <div className="space-y-8">
        {/* Stats Section */}
        <section>
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            <StatCard
              label="Organizations"
              value={orgsWithProjects.length}
              icon={<IconBuilding className="size-5" />}
            />
            <StatCard
              label="Projects"
              value={totalProjects}
              icon={<IconFolder className="size-5" />}
            />
            <StatCard
              label="AI Agents"
              value={agentStats?.totalAgents ?? 0}
              icon={<IconRobot className="size-5" />}
            />
            <StatCard
              label="Active Runs"
              value={agentStats?.activeRuns ?? 0}
              icon={<IconPlayerPlay className="size-5" />}
              showPulse
            />
          </div>
        </section>

        {/* GitHub Issues Overview */}
        <IssuesOverviewCard />

        {/* Organizations and Projects */}
        {hasOrganizations ? (
          <section>
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-sm font-medium text-muted-foreground">
                Organizations
              </h2>
              <CreateOrganizationDialog />
            </div>

            <div className="space-y-4">
              {orgsWithProjects.map((orgWithProjects) => {
                const userRole = orgWithProjects.userRole;
                const isAdmin =
                  userRole === OrganizationRoles.Owner ||
                  userRole === OrganizationRoles.Admin;
                const isMember =
                  userRole === OrganizationRoles.Member;

                return (
                  <Card
                    key={orgWithProjects.organization.id}
                    className="overflow-hidden"
                  >
                    {/* Organization Header */}
                    <div className="flex items-center justify-between border-b border-border bg-muted/30 px-4 py-3">
                      <div className="flex min-w-0 items-center gap-3">
                        <div className="flex size-8 shrink-0 items-center justify-center bg-primary/10 text-primary">
                          <IconBuilding className="size-4" />
                        </div>
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="truncate font-medium">
                              {
                                orgWithProjects.organization
                                  .name
                              }
                            </span>
                            {isMember && (
                              <Badge
                                size="compact"
                                variant="secondary"
                              >
                                Member
                              </Badge>
                            )}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {
                              orgWithProjects.projects
                                .length
                            }{" "}
                            project
                            {orgWithProjects.projects
                              .length !== 1
                              ? "s"
                              : ""}
                          </div>
                        </div>
                      </div>
                      {isAdmin && (
                        <Link
                          to="/app/organization/$id"
                          params={{
                            id: orgWithProjects.organization
                              .id,
                          }}
                        >
                          <Button
                            variant="ghost"
                            size="sm"
                            spacing="compact"
                          >
                            Manage
                            <IconChevronRight className="size-3" />
                          </Button>
                        </Link>
                      )}
                    </div>

                    {/* Projects Grid */}
                    <CardContent variant="p4">
                      {orgWithProjects.projects.length >
                      0 ? (
                        <>
                          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                            {orgWithProjects.projects.map(
                              (project) => (
                                <ProjectCard
                                  key={project.id}
                                  project={project}
                                />
                              )
                            )}
                          </div>
                          {isAdmin && (
                            <div className="mt-3">
                              <CreateProjectDialog
                                organizationId={
                                  orgWithProjects
                                    .organization.id
                                }
                              />
                            </div>
                          )}
                        </>
                      ) : (
                        <Empty variant="tall">
                          <EmptyHeader>
                            <EmptyTitle>
                              No projects yet
                            </EmptyTitle>
                            {isMember && (
                              <EmptyDescription>
                                Contact an admin to create
                                projects
                              </EmptyDescription>
                            )}
                          </EmptyHeader>
                          {isAdmin && (
                            <EmptyContent>
                              <CreateProjectDialog
                                organizationId={
                                  orgWithProjects
                                    .organization.id
                                }
                              />
                            </EmptyContent>
                          )}
                        </Empty>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </section>
        ) : (
          <Empty variant="taller">
            <EmptyHeader>
              <EmptyTitle>Welcome to vivus</EmptyTitle>
              <EmptyDescription>
                Get started by creating your first
                organization.
              </EmptyDescription>
            </EmptyHeader>
            <EmptyContent>
              <CreateOrganizationDialog />
            </EmptyContent>
          </Empty>
        )}
      </div>

      <Suspense fallback={null}>
        <PendingInvitationModal />
      </Suspense>
    </AppLayout>
  );
}
